/* eslint-disable react-hooks/exhaustive-deps */
/**
 * BrandAdmin — v1.3 spec §+18 brand-kit admin route.
 *
 * MVP — 4 of 11 tabs land in this iteration:
 *   • Palette         — view/edit `brand_typography.roles_config.palette`
 *   • Templates       — visual grid of all post_templates with retire control
 *   • References      — designer_references audit queue (lift pending → audited)
 *   • Voice & rules   — voice profile + constraints
 *
 * Remaining tabs (logos, fonts, decorations, backgrounds, lockups, analytics,
 * subjects, illustrations) will hang off the same shell when they ship.
 */
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { CheckCircle2, AlertTriangle, Save, Loader2, Upload, Trash2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import ImageTreatmentsTab from "../components/brand/ImageTreatmentsTab";

const _API_BASE =
  typeof window !== "undefined" && window.location.hostname !== "localhost"
    ? window.location.origin
    : process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const API = `${_API_BASE}/api`;

export default function BrandAdminPage() {
  const { workspaceId: routeWs } = useParams();
  const [searchParams] = useSearchParams();
  const workspaceId =
    routeWs ||
    searchParams.get("ws") ||
    "a24c8722-c708-4511-a5cc-8b98a884bbfb";

  const [tab, setTab] = useState("palette");

  return (
    <div className="container mx-auto p-4 sm:p-6 max-w-6xl" data-testid="brand-admin-page">
      <div className="flex items-baseline justify-between mb-6">
        <h1 className="text-2xl font-semibold">Brand kit</h1>
        <Badge variant="outline" data-testid="brand-admin-ws">{workspaceId.slice(0, 12)}…</Badge>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="palette" data-testid="tab-palette">Palette</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">Templates</TabsTrigger>
          <TabsTrigger value="references" data-testid="tab-references">References</TabsTrigger>
          <TabsTrigger value="voice" data-testid="tab-voice">Voice & rules</TabsTrigger>
          <TabsTrigger value="logos" data-testid="tab-logos">Logos</TabsTrigger>
          <TabsTrigger value="fonts" data-testid="tab-fonts">Fonts</TabsTrigger>
          <TabsTrigger value="decorations" data-testid="tab-decorations">Decorations</TabsTrigger>
          <TabsTrigger value="backgrounds" data-testid="tab-backgrounds">Backgrounds</TabsTrigger>
          <TabsTrigger value="subjects" data-testid="tab-subjects">Subjects</TabsTrigger>
          <TabsTrigger value="illustrations" data-testid="tab-illustrations">Illustrations</TabsTrigger>
          <TabsTrigger value="lockups" data-testid="tab-lockups">Lockups</TabsTrigger>
          <TabsTrigger value="treatments" data-testid="tab-treatments">Treatments</TabsTrigger>
          <TabsTrigger value="outcomes" data-testid="tab-outcomes">Engagement</TabsTrigger>
        </TabsList>
        <TabsContent value="palette" className="pt-4">
          <PaletteTab workspaceId={workspaceId} />
        </TabsContent>
        <TabsContent value="templates" className="pt-4">
          <TemplatesTab workspaceId={workspaceId} />
        </TabsContent>
        <TabsContent value="references" className="pt-4">
          <ReferencesTab workspaceId={workspaceId} />
        </TabsContent>
        <TabsContent value="voice" className="pt-4">
          <VoiceAndRulesTab workspaceId={workspaceId} />
        </TabsContent>
        {["logos", "fonts", "decorations", "backgrounds", "subjects", "illustrations", "lockups"].map((pool) => (
          <TabsContent key={pool} value={pool} className="pt-4">
            <AssetPoolTab workspaceId={workspaceId} pool={pool} />
          </TabsContent>
        ))}
        <TabsContent value="outcomes" className="pt-4">
          <EngagementOutcomesTab workspaceId={workspaceId} />
        </TabsContent>
        <TabsContent value="treatments" className="pt-4">
          <ImageTreatmentsTab workspaceId={workspaceId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ─── Palette tab ──────────────────────────────────────────────────────────
function PaletteTab({ workspaceId }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    fetch(`${API}/v2/workspaces/${workspaceId}/brand/typography`, { credentials: "include" })
      .then((r) => r.json())
      .then(setCfg)
      .catch(() => toast.error("Failed to load brand typography"));
  }, [workspaceId]);

  if (!cfg) return <Skeleton className="h-32 w-full" />;
  const palette = cfg.roles_config?.palette || {};

  const updateToken = (token, hex) => {
    const next = { ...cfg.roles_config, palette: { ...palette, [token]: hex } };
    setCfg({ ...cfg, roles_config: next });
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/brand/typography`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ roles_config: cfg.roles_config }),
      });
      if (!r.ok) throw new Error("save failed");
      toast.success("Palette saved");
    } catch (e) {
      toast.error(e?.message || "save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="palette-tab"><CardContent className="p-4 space-y-3">
      <div className="grid sm:grid-cols-2 gap-3">
        {Object.entries(palette).map(([token, hex]) => (
          <div key={token} className="flex items-center gap-2" data-testid={`palette-row-${token}`}>
            <div className="w-10 h-10 rounded border" style={{ background: hex }} />
            <div className="flex-1">
              <div className="text-xs font-mono text-muted-foreground">{token}</div>
              <Input
                data-testid={`palette-hex-${token}`}
                value={hex || ""}
                onChange={(e) => updateToken(token, e.target.value)}
              />
            </div>
          </div>
        ))}
      </div>
      <div className="flex justify-end">
        <Button data-testid="palette-save" onClick={save} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Save palette
        </Button>
      </div>
    </CardContent></Card>
  );
}

// ─── Templates tab ────────────────────────────────────────────────────────
function TemplatesTab({ workspaceId }) {
  const [tpls, setTpls] = useState([]);
  const reload = () =>
    fetch(`${API}/v2/workspaces/${workspaceId}/templates`, { credentials: "include" })
      .then((r) => r.json())
      .then(setTpls)
      .catch(() => toast.error("Failed to load templates"));
  useEffect(() => { reload(); }, [workspaceId]);

  return (
    <div data-testid="templates-tab" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {tpls.map((t) => (
        <Card key={t.template_id} data-testid={`admin-template-${t.template_id}`} className="overflow-hidden">
          <img
            src={`${API}/v2/workspaces/${workspaceId}/templates/${t.template_id}/preview?width=720&height=720`}
            alt={t.name}
            className="w-full aspect-square object-cover bg-muted"
          />
          <CardContent className="p-3 space-y-1">
            <div className="font-medium text-sm">{t.name}</div>
            <div className="text-xs text-muted-foreground font-mono">{t.template_id}</div>
            <div className="text-xs text-muted-foreground">family: {t.family} · slots: {t.slots?.length || 0}</div>
            <div className="flex flex-wrap gap-1 mt-2">
              {(t.purposes || []).slice(0, 3).map((p) => (
                <Badge key={p} variant="outline" className="text-[10px]">{p}</Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
      {!tpls.length ? <p className="text-sm text-muted-foreground italic col-span-full">No templates yet.</p> : null}
    </div>
  );
}

// ─── References tab ───────────────────────────────────────────────────────
function ReferencesTab({ workspaceId }) {
  const [refs, setRefs] = useState([]);
  const [filter, setFilter] = useState("pending_audit");
  const [uploading, setUploading] = useState(false);
  const [purpose, setPurpose] = useState("");
  const [moodTags, setMoodTags] = useState("");
  const fileInputRef = useRef(null);
  const reload = () =>
    fetch(
      `${API}/v2/workspaces/${workspaceId}/brand/designer-references?audit_status=${filter}`,
      { credentials: "include" }
    )
      .then((r) => r.json())
      .then(setRefs)
      .catch(() => toast.error("Failed to load references"));
  useEffect(() => { reload(); }, [workspaceId, filter]);

  const auditRow = async (refId, status) => {
    const r = await fetch(
      `${API}/v2/workspaces/${workspaceId}/brand/designer-references/${refId}/audit`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ audit_status: status }),
      }
    );
    if (!r.ok) toast.error("audit failed");
    else { toast.success(status === "audited" ? "Audited" : "Rejected"); reload(); }
  };

  const onUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (purpose.trim()) fd.append("purpose", purpose.trim());
      if (moodTags.trim()) fd.append("mood_tags", moodTags.trim());
      const r = await fetch(
        `${API}/v2/workspaces/${workspaceId}/brand/designer-references/upload`,
        { method: "POST", credentials: "include", body: fd }
      );
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j?.detail || "upload failed");
      }
      toast.success("Reference uploaded — pending audit");
      setPurpose("");
      setMoodTags("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      setFilter("pending_audit");
      reload();
    } catch (e) {
      toast.error(e?.message || "upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid="references-tab" className="space-y-3">
      <Card data-testid="reference-upload-card"><CardContent className="p-4 space-y-2">
        <h3 className="text-sm font-medium">Upload designer reference</h3>
        <div className="grid sm:grid-cols-2 gap-2">
          <Input
            data-testid="reference-upload-purpose"
            placeholder="Purpose (e.g. team_milestone)"
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
          />
          <Input
            data-testid="reference-upload-mood"
            placeholder="Mood tags (comma-separated)"
            value={moodTags}
            onChange={(e) => setMoodTags(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2">
          <Input
            ref={fileInputRef}
            data-testid="reference-upload-file"
            type="file"
            accept="image/*"
            disabled={uploading}
            onChange={(e) => onUpload(e.target.files?.[0])}
          />
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        </div>
        <p className="text-xs text-muted-foreground italic">
          Uploads land in <span className="font-mono">pending_audit</span> per spec §+12.2.
          Lift to <span className="font-mono">audited</span> below before they're eligible for use.
        </p>
      </CardContent></Card>

      <div className="flex gap-2">
        {["pending_audit", "audited", "rejected"].map((s) => (
          <Button
            key={s}
            size="sm"
            variant={filter === s ? "default" : "outline"}
            onClick={() => setFilter(s)}
            data-testid={`references-filter-${s}`}
          >
            {s.replace("_", " ")}
          </Button>
        ))}
        <Badge variant="outline" className="ml-auto" data-testid="references-count">{refs.length}</Badge>
      </div>
      <div className="grid sm:grid-cols-3 lg:grid-cols-4 gap-2">
        {refs.map((r) => (
          <Card key={r.reference_id} data-testid={`reference-card-${r.reference_id}`}>
            <CardContent className="p-2 space-y-2">
              <div className="aspect-square bg-muted rounded text-xs flex items-center justify-center text-muted-foreground overflow-hidden">
                {r.image_path?.startsWith("cards/") ? (
                  <img
                    alt="ref"
                    src={`${API}/v2/storage/serve?path=${encodeURIComponent(r.image_path)}`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span>{r.image_path?.split("/").pop()?.slice(0, 22) || "—"}</span>
                )}
              </div>
              {filter === "pending_audit" ? (
                <div className="flex gap-1">
                  <Button size="sm" variant="default" className="flex-1" onClick={() => auditRow(r.reference_id, "audited")} data-testid={`reference-audit-approve-${r.reference_id}`}>
                    <CheckCircle2 className="h-3 w-3 mr-1" /> Audit
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => auditRow(r.reference_id, "rejected")} data-testid={`reference-audit-reject-${r.reference_id}`}>
                    <AlertTriangle className="h-3 w-3" />
                  </Button>
                </div>
              ) : (
                <Badge variant={filter === "audited" ? "default" : "destructive"} className="text-xs">{r.audit_status}</Badge>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ─── Voice & constraints tab ──────────────────────────────────────────────
function VoiceAndRulesTab({ workspaceId }) {
  const [voiceProfile, setVoiceProfile] = useState({});
  const [constraints, setConstraints] = useState({});
  const [smokeCopy, setSmokeCopy] = useState("");
  const [smokeResult, setSmokeResult] = useState(null);
  const [smokeLoading, setSmokeLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/v2/workspaces/${workspaceId}/brand/voice-profile`, { credentials: "include" }).then(r => r.json()),
      fetch(`${API}/v2/workspaces/${workspaceId}/brand/constraints`, { credentials: "include" }).then(r => r.json()),
    ]).then(([v, c]) => { setVoiceProfile(v.voice_profile || {}); setConstraints(c.constraints || {}); });
  }, [workspaceId]);

  const saveVoice = async () => {
    const r = await fetch(`${API}/v2/workspaces/${workspaceId}/brand/voice-profile`, {
      method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify({ voice_profile: voiceProfile }),
    });
    toast[r.ok ? "success" : "error"](r.ok ? "Voice profile saved" : "save failed");
  };
  const saveConstraints = async () => {
    const r = await fetch(`${API}/v2/workspaces/${workspaceId}/brand/constraints`, {
      method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify({ constraints }),
    });
    toast[r.ok ? "success" : "error"](r.ok ? "Constraints saved" : "save failed");
  };

  const smoke = async () => {
    if (!smokeCopy.trim()) return;
    setSmokeLoading(true);
    try {
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/copy/voice-check`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ copy: smokeCopy }),
      });
      setSmokeResult(await r.json());
    } finally { setSmokeLoading(false); }
  };

  const constraintFields = [
    ["max_decoration_density", "Max decoration density"],
    ["max_text_coverage", "Max text coverage (0..1)"],
    ["max_accent_color_count", "Max accent colours"],
    ["min_whitespace_ratio", "Min whitespace ratio (0..1)"],
    ["max_effects_per_slot", "Max effects per slot"],
    ["max_font_roles_per_post", "Max font roles per post"],
  ];

  return (
    <div data-testid="voice-tab" className="space-y-4">
      <Card><CardContent className="p-4 space-y-3">
        <h3 className="text-sm font-medium">Voice profile</h3>
        <div className="grid sm:grid-cols-2 gap-2">
          <Input data-testid="voice-name" placeholder="profile name" value={voiceProfile?.name || ""} onChange={(e) => setVoiceProfile({ ...voiceProfile, name: e.target.value })} />
          <Input data-testid="voice-tone" placeholder="tone (e.g. measured, specific, warm)" value={voiceProfile?.tone || ""} onChange={(e) => setVoiceProfile({ ...voiceProfile, tone: e.target.value })} />
        </div>
        <Textarea data-testid="voice-rules" rows={3} placeholder="Rules — one per line" value={voiceProfile?.rules || ""} onChange={(e) => setVoiceProfile({ ...voiceProfile, rules: e.target.value })} />
        <div className="flex justify-end">
          <Button data-testid="voice-save" onClick={saveVoice}><Save className="h-4 w-4 mr-1" /> Save voice profile</Button>
        </div>
      </CardContent></Card>

      <Card><CardContent className="p-4 space-y-3">
        <h3 className="text-sm font-medium">Taste constraints</h3>
        <div className="grid sm:grid-cols-2 gap-2">
          {constraintFields.map(([k, label]) => (
            <div key={k} className="space-y-1">
              <div className="text-xs text-muted-foreground">{label}</div>
              <Input
                data-testid={`constraint-${k}`}
                value={constraints?.[k] ?? ""}
                onChange={(e) => setConstraints({ ...constraints, [k]: parseFloat(e.target.value) || e.target.value })}
              />
            </div>
          ))}
        </div>
        <div className="flex justify-end">
          <Button data-testid="constraints-save" onClick={saveConstraints}><Save className="h-4 w-4 mr-1" /> Save constraints</Button>
        </div>
      </CardContent></Card>

      <Card><CardContent className="p-4 space-y-3">
        <h3 className="text-sm font-medium">Smoke-test the voice classifier</h3>
        <Textarea data-testid="voice-smoke-input" rows={3} placeholder="Paste a draft caption to classify..." value={smokeCopy} onChange={(e) => setSmokeCopy(e.target.value)} />
        <div className="flex justify-end">
          <Button data-testid="voice-smoke-run" onClick={smoke} disabled={smokeLoading || !smokeCopy.trim()}>
            {smokeLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Run voice check
          </Button>
        </div>
        {smokeResult ? (
          <div data-testid="voice-smoke-result" className="text-xs space-y-1 p-3 rounded bg-muted">
            <div>Verdict: <span className="font-mono">{smokeResult.verdict}</span></div>
            <div className="text-muted-foreground">{(smokeResult.issues || []).length} issues</div>
            {(smokeResult.issues || []).slice(0, 4).map((i, idx) => (
              <div key={idx}>
                <Badge variant={i.severity === "fail" ? "destructive" : "secondary"} className="text-[10px] mr-1">{i.severity}</Badge>
                <span className="font-mono text-[10px]">{i.category}</span>
                {i.span ? <span className="text-muted-foreground">: “{i.span}”</span> : null}
              </div>
            ))}
          </div>
        ) : null}
      </CardContent></Card>
    </div>
  );
}


// Suggested tag vocabularies — locked v1.3 tag conventions per pool.
// `null` means the pool is free-form (no canonical tag chips).
const POOL_TAG_SUGGESTIONS = {
  logos: [
    "primary", "monochrome_white", "monochrome_black", "blue",
    "with_tagline", "no_tagline", "wordmark", "icon",
  ],
  fonts: ["display", "body", "script_accent", "mono"],
  decorations: ["scatter", "accent", "organic", "geometric"],
  backgrounds: ["plate", "texture", "gradient", "pattern"],
  subjects: ["portrait", "candid", "studio", "editorial"],
  illustrations: ["spot", "hero", "icon"],
  lockups: ["proposal_cover", "case_study", "deck_title"],
};

// ─── Generic asset-pool tab (logos / fonts / decorations / backgrounds /
//     subjects / illustrations / lockups). All seven pools share schema:
//     each row is a brand_assets entry tagged with `pool=<name>`.
function AssetPoolTab({ workspaceId, pool }) {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [tagDraft, setTagDraft] = useState("");
  const fileInputRef = useRef(null);

  const reload = () => {
    setLoading(true);
    fetch(`${API}/v2/workspaces/${workspaceId}/brand/assets?pool=${pool}`, { credentials: "include" })
      .then((r) => r.json())
      .then((d) => setAssets(Array.isArray(d) ? d : []))
      .catch(() => toast.error(`Failed to load ${pool}`))
      .finally(() => setLoading(false));
  };
  useEffect(() => { reload(); }, [workspaceId, pool]);

  const onUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("pool", pool);
      if (tagDraft.trim()) fd.append("tags", tagDraft.trim());
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/brand/assets/upload`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j?.detail || "upload failed");
      }
      toast.success(`Uploaded to ${pool}`);
      setTagDraft("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      reload();
    } catch (e) {
      toast.error(e?.message || "upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onRetire = async (assetId) => {
    const r = await fetch(`${API}/v2/workspaces/${workspaceId}/brand/assets/${assetId}`, {
      method: "DELETE",
      credentials: "include",
    });
    if (!r.ok) toast.error("retire failed");
    else { toast.success("Retired"); reload(); }
  };

  const isFontPool = pool === "fonts";
  const accept = isFontPool
    ? ".ttf,.otf,.woff,.woff2,font/ttf,font/otf,font/woff,font/woff2"
    : "image/*";

  return (
    <div data-testid={`asset-pool-tab-${pool}`} className="space-y-4">
      <Card data-testid={`asset-upload-card-${pool}`}>
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center gap-2">
            <Upload className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-medium capitalize">Upload {pool}</h3>
          </div>
          <Input
            data-testid={`asset-upload-tags-${pool}`}
            placeholder="Tags (comma-separated, e.g. primary,wordmark)"
            value={tagDraft}
            onChange={(e) => setTagDraft(e.target.value)}
            disabled={uploading}
          />
          {POOL_TAG_SUGGESTIONS[pool]?.length ? (
            <div className="flex flex-wrap gap-1" data-testid={`asset-tag-chips-${pool}`}>
              {POOL_TAG_SUGGESTIONS[pool].map((t) => {
                const present = tagDraft.split(",").map((x) => x.trim()).includes(t);
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => {
                      const cur = tagDraft.split(",").map((x) => x.trim()).filter(Boolean);
                      const next = present ? cur.filter((x) => x !== t) : [...cur, t];
                      setTagDraft(next.join(","));
                    }}
                    className={`text-[10px] px-2 py-0.5 rounded border transition ${
                      present
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-background text-muted-foreground border-border hover:border-blue-400"
                    }`}
                    data-testid={`asset-tag-chip-${pool}-${t}`}
                  >
                    {t}
                  </button>
                );
              })}
            </div>
          ) : null}
          <div className="flex items-center gap-2">
            <Input
              ref={fileInputRef}
              data-testid={`asset-upload-input-${pool}`}
              type="file"
              accept={accept}
              disabled={uploading}
              onChange={(e) => onUpload(e.target.files?.[0])}
            />
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          </div>
          <p className="text-[11px] text-muted-foreground italic">
            {isFontPool
              ? "Upload .ttf / .otf / .woff / .woff2. Family/weight metadata can be set after upload."
              : "Images upload to object storage and become available to the compositor immediately."}
          </p>
        </CardContent>
      </Card>

      {loading ? (
        <Skeleton className="h-32 w-full" />
      ) : assets.length === 0 ? (
        <p className="text-sm text-muted-foreground italic" data-testid={`asset-pool-empty-${pool}`}>
          No {pool} yet. Upload above to seed this pool.
        </p>
      ) : (
        <div className="grid sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid={`asset-pool-grid-${pool}`}>
          {assets.map((a) => (
            <Card key={a.asset_id} data-testid={`asset-card-${a.asset_id}`}>
              <CardContent className="p-2 space-y-2">
                <div className="aspect-square bg-muted rounded overflow-hidden flex items-center justify-center">
                  {isFontPool ? (
                    <div className="text-center p-2">
                      <ImageIcon className="h-6 w-6 mx-auto text-muted-foreground" />
                      <div className="text-[10px] font-mono mt-1 break-all">
                        {(a.asset_metadata?.filename || a.storage_path?.split("/").pop() || "font").slice(0, 28)}
                      </div>
                    </div>
                  ) : a.storage_path?.startsWith("cards/") ? (
                    <img
                      alt={a.tags?.[0] || pool}
                      src={`${API}/v2/storage/serve?path=${encodeURIComponent(a.storage_path)}`}
                      className="w-full h-full object-contain bg-zinc-50"
                    />
                  ) : (
                    <div className="text-[10px] text-muted-foreground p-2 text-center break-all">
                      {a.storage_path}
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-1">
                  {(a.tags || []).slice(0, 3).map((t) => (
                    <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>
                  ))}
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[10px] font-mono text-muted-foreground truncate max-w-[60%]">
                    {a.asset_id?.slice(0, 8)}…
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onRetire(a.asset_id)}
                    data-testid={`asset-retire-${a.asset_id}`}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Engagement Outcomes (Phase 13) — manual entry + per-template aggregate
function EngagementOutcomesTab({ workspaceId }) {
  const [byTemplate, setByTemplate] = useState({});
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    card_id: "", channel_id: "linkedin-main", service: "linkedin",
    published_at: new Date().toISOString().slice(0, 16),
    impressions: "", likes: "", comments: "", shares: "", saves: "",
    metrics_window: "7d",
  });
  const [submitting, setSubmitting] = useState(false);

  const reload = () => {
    setLoading(true);
    fetch(`${API}/v2/workspaces/${workspaceId}/post-outcomes/by-template`, { credentials: "include" })
      .then((r) => r.json())
      .then((d) => setByTemplate(d || {}))
      .catch(() => toast.error("Failed to load outcomes"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { reload(); }, [workspaceId]);

  const submit = async () => {
    if (!form.card_id.trim()) {
      toast.error("card_id is required");
      return;
    }
    setSubmitting(true);
    try {
      const metrics = {};
      ["impressions", "likes", "comments", "shares", "saves"].forEach((k) => {
        if (form[k] !== "" && !isNaN(Number(form[k]))) metrics[k] = Number(form[k]);
      });
      const body = {
        card_id: form.card_id.trim(),
        channel_id: form.channel_id || "linkedin-main",
        service: form.service,
        published_at: new Date(form.published_at).toISOString(),
        metrics,
        metrics_window: form.metrics_window || "7d",
      };
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/post-outcomes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j?.detail || "submit failed");
      }
      toast.success("Outcome recorded");
      setForm({ ...form, card_id: "", impressions: "", likes: "", comments: "", shares: "", saves: "" });
      reload();
    } catch (e) {
      toast.error(e?.message || "submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  const onCsvUpload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/post-outcomes/upload-csv`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.detail || "csv upload failed");
      toast.success(`Imported ${j.imported || 0} rows · skipped ${j.skipped || 0}`);
      reload();
    } catch (e) {
      toast.error(e?.message || "csv upload failed");
    }
  };

  const templateRows = Object.entries(byTemplate);

  return (
    <div data-testid="outcomes-tab" className="space-y-4">
      <Card data-testid="outcome-entry-card"><CardContent className="p-4 space-y-3">
        <h3 className="text-sm font-medium">Record post outcome (manual)</h3>
        <div className="grid sm:grid-cols-2 gap-2">
          <Input data-testid="outcome-card-id" placeholder="card_id" value={form.card_id} onChange={(e) => setForm({ ...form, card_id: e.target.value })} />
          <select
            data-testid="outcome-service"
            className="border rounded h-9 px-2 text-sm bg-background"
            value={form.service}
            onChange={(e) => setForm({ ...form, service: e.target.value })}
          >
            <option value="linkedin">linkedin</option>
            <option value="instagram">instagram</option>
            <option value="x">x</option>
          </select>
          <Input data-testid="outcome-channel" placeholder="channel_id" value={form.channel_id} onChange={(e) => setForm({ ...form, channel_id: e.target.value })} />
          <Input
            data-testid="outcome-published-at"
            type="datetime-local"
            value={form.published_at}
            onChange={(e) => setForm({ ...form, published_at: e.target.value })}
          />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          {["impressions", "likes", "comments", "shares", "saves"].map((k) => (
            <Input
              key={k}
              data-testid={`outcome-metric-${k}`}
              placeholder={k}
              type="number"
              value={form[k]}
              onChange={(e) => setForm({ ...form, [k]: e.target.value })}
            />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="outcome-window"
            className="border rounded h-9 px-2 text-sm bg-background"
            value={form.metrics_window}
            onChange={(e) => setForm({ ...form, metrics_window: e.target.value })}
          >
            <option value="24h">24h</option>
            <option value="7d">7d</option>
            <option value="30d">30d</option>
          </select>
          <Button onClick={submit} disabled={submitting} data-testid="outcome-submit">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Record
          </Button>
        </div>
      </CardContent></Card>

      <Card data-testid="outcome-csv-card"><CardContent className="p-4 space-y-2">
        <h3 className="text-sm font-medium">Bulk CSV import</h3>
        <p className="text-xs text-muted-foreground">
          CSV columns: <code>card_id, service, channel_id, published_at, impressions, likes, comments, shares, saves, metrics_window</code>
        </p>
        <Input
          data-testid="outcome-csv-input"
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => onCsvUpload(e.target.files?.[0])}
        />
      </CardContent></Card>

      <Card data-testid="outcome-aggregate-card"><CardContent className="p-4 space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium">Per-template engagement (rolling)</h3>
          <Button size="sm" variant="ghost" onClick={reload} data-testid="outcome-aggregate-refresh">Refresh</Button>
        </div>
        {loading ? (
          <Skeleton className="h-16 w-full" />
        ) : templateRows.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">No outcomes recorded yet.</p>
        ) : (
          <table className="w-full text-xs" data-testid="outcome-aggregate-table">
            <thead>
              <tr className="text-left text-muted-foreground">
                <th className="py-1">Template</th>
                <th className="py-1">Posts</th>
                <th className="py-1">Avg impressions</th>
                <th className="py-1">Avg engagement</th>
              </tr>
            </thead>
            <tbody>
              {templateRows.map(([tid, row]) => (
                <tr key={tid} className="border-t" data-testid={`outcome-row-${tid}`}>
                  <td className="py-1 font-mono">{tid}</td>
                  <td className="py-1">{row.posts_count}</td>
                  <td className="py-1">{row.avg_impressions}</td>
                  <td className="py-1">{row.avg_engagement_actions}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent></Card>
    </div>
  );
}
